import { defineStore } from 'pinia';
import { useRoute } from 'vue-router';
import type { identifier } from '@core/models/base';
import type { FeaturesDisplayModel } from '@core/models/features';
import { availableFeaturesFromStudioId, availableFeaturesFromStudioSlug } from '@shared/services/features.service';
import { useOptionalRouteIds } from '@shared/composables/useRouteIds';

export interface FeaturesState {
  initialized: boolean;
  loading: boolean;
  studioId: identifier;
  studioSlug: string | undefined;
  features: FeaturesDisplayModel[];
}

export type FeatureIdentifier =
  | 'rspoJob'
  | 'pkgmgmt'
  | 'subjectManagement'
  | 'yearbook'
  | 'studioAccessToken'
  | 'studioVerification';

export const useFeaturesStore = defineStore('features', {
  state: (): FeaturesState => ({
    initialized: false,
    loading: false,
    studioId: 0,
    studioSlug: '',
    features: [],
  }),

  getters: {
    featureIdentifiers(state) {
      return new Set(state.features.map((feature) => feature.identifier));
    },
  },

  actions: {
    async initialize() {
      const { studioSlug } = useRoute().params;
      const [studioId] = useOptionalRouteIds('studioId');
      if (studioSlug !== (this.studioSlug as string) && studioId !== this.studioId) {
        this.loading = true;
        if (studioSlug) {
          this.studioSlug = studioSlug as string;
          this.features = await availableFeaturesFromStudioSlug(studioSlug as string);
        } else if (studioId != null) {
          this.studioId = studioId;
          this.features = await availableFeaturesFromStudioId(studioId);
        }
      }
      this.loading = false;
      this.initialized = true;
    },

    async initializeWithStudioId(studioId: identifier) {
      if (studioId !== this.studioId) {
        this.loading = true;
        this.studioId = studioId;
        this.features = await availableFeaturesFromStudioId(studioId);
        this.loading = false;
        this.initialized = true;
      }
    },

    async refresh() {
      try {
        this.loading = true;
        if (this.studioSlug) {
          this.features = await availableFeaturesFromStudioSlug(this.studioSlug);
        } else if (this.studioId) {
          this.features = await availableFeaturesFromStudioId(this.studioId);
        }
      } finally {
        this.loading = false;
      }
    },

    hasFeature(featureId: FeatureIdentifier) {
      return this.featureIdentifiers.has(featureId);
    },

    clear() {
      this.studioId = 0;
      this.studioSlug = '';
      this.features = [];
    },
  },
});
